<script lang="ts">
  import type { Event, HistoricalDate } from '../lib/types'
  import { Dialog, Input, Textarea, Label, Button, Checkbox } from '$lib/components/ui'

  interface Props {
    event?: Event | null
    videoId: string
    onSave: (event: Event) => void
    onDelete?: (eventId: number) => void
    onCancel: () => void
  }

  let { event = null, videoId, onSave, onDelete, onCancel }: Props = $props()

  let name = $state(event?.name || '')
  let description = $state(event?.description || '')
  let open = $state(true)

  const DATE_RANGE_MIN = -9999
  const DATE_RANGE_MAX = 9999

  function isYearInDateRange(year: number): boolean {
    return year >= DATE_RANGE_MIN && year <= DATE_RANGE_MAX
  }

  function initYear(year: number | undefined): { display: string, isBc: boolean } {
    if (year === undefined) return { display: '', isBc: false }
    if (year < 0) return { display: Math.abs(year).toString(), isBc: true }
    return { display: year.toString(), isBc: false }
  }

  let startYearInit = initYear(event?.dateStart?.year)
  let startYear = $state(startYearInit.display)
  let startIsBc = $state(startYearInit.isBc)
  let startMonth = $state(event?.dateStart?.month?.toString() || '')
  let startDay = $state(event?.dateStart?.day?.toString() || '')

  let endYearInit = initYear(event?.dateEnd?.year)
  let endYear = $state(endYearInit.display)
  let endIsBc = $state(endYearInit.isBc)
  let endMonth = $state(event?.dateEnd?.month?.toString() || '')
  let endDay = $state(event?.dateEnd?.day?.toString() || '')

  function getActualYear(display: string, isBc: boolean): number {
    const y = parseInt(display) || 0
    return isBc ? -Math.abs(y) : Math.abs(y)
  }

  let startYearActual = $derived(getActualYear(startYear, startIsBc))
  let endYearActual = $derived(getActualYear(endYear, endIsBc))
  let startDateEnabled = $derived(startYear !== '' && isYearInDateRange(startYearActual))
  let endDateEnabled = $derived(endYear !== '' && isYearInDateRange(endYearActual))

  $effect(() => {
    if (!startDateEnabled) {
      startMonth = ''
      startDay = ''
    }
  })

  $effect(() => {
    if (!endDateEnabled) {
      endMonth = ''
      endDay = ''
    }
  })

  $effect(() => {
    if (startYear.startsWith('+')) {
      startYear = startYear.slice(1)
      startIsBc = false
    } else if (startYear.startsWith('-')) {
      startYear = startYear.slice(1)
      startIsBc = true
    }
  })

  $effect(() => {
    if (endYear.startsWith('+')) {
      endYear = endYear.slice(1)
      endIsBc = false
    } else if (endYear.startsWith('-')) {
      endYear = endYear.slice(1)
      endIsBc = true
    }
  })
  let participants = $state(event?.participants?.join(', ') || '')
  let isPersonalEvent = $state(event?.isPersonalEvent || false)
  let internalOnly = $state(event?.internalOnly || false)
  let isSaving = $state(false)
  let isDeleting = $state(false)

  function buildDate(year: string, isBc: boolean, month: string, day: string): HistoricalDate | null {
    const y = parseInt(year)
    if (isNaN(y)) return null
    const actualYear = isBc ? -Math.abs(y) : Math.abs(y)
    const result: HistoricalDate = { year: actualYear }
    if (isYearInDateRange(actualYear)) {
      const m = parseInt(month)
      if (!isNaN(m) && m >= 1 && m <= 12) result.month = m
      const d = parseInt(day)
      if (!isNaN(d) && d >= 1 && d <= 31) result.day = d
    }
    return result
  }

  async function handleSave() {
    if (!name.trim() || !startYear.trim()) return

    isSaving = true
    try {
      const dateStart = buildDate(startYear, startIsBc, startMonth, startDay)
      const dateEnd = buildDate(endYear, endIsBc, endMonth, endDay)
      const participantList = participants
        .split(',')
        .map(p => p.trim())
        .filter(p => p.length > 0)

      const payload = {
        name: name.trim(),
        description: description.trim(),
        dateStart,
        dateEnd,
        participants: participantList,
        isPersonalEvent,
        internal_only: internalOnly,
        video_id: videoId,
        mentions: event?.mentions?.map(m => m.timecode) || [],
      }

      const isNew = !event?.id
      const url = isNew
        ? '/api/events'
        : `/api/events/${event.id}`

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        const savedEvent = await response.json()
        onSave(savedEvent)
      }
    } finally {
      isSaving = false
    }
  }

  async function handleDelete() {
    if (!event?.id || !onDelete) return
    if (!confirm(`Weet je zeker dat je "${event.name}" wilt verwijderen?`)) return

    isDeleting = true
    try {
      const response = await fetch(`/api/events/${event.id}`, {
        method: 'DELETE',
      })
      if (response.ok) {
        onDelete(event.id)
      }
    } finally {
      isDeleting = false
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) onCancel()
  }
</script>

<Dialog bind:open onOpenChange={handleOpenChange} title={event ? 'Edit event' : 'New event'}>
  <div class="space-y-4">
    <div>
      <Label for="name">Name</Label>
      <Input id="name" bind:value={name} placeholder="Event name" />
    </div>

    <div>
      <Label for="description">Description</Label>
      <Textarea id="description" bind:value={description} placeholder="Description" rows={3} />
    </div>

    <div class="grid grid-cols-2 gap-4">
      <div>
        <Label>Start date</Label>
        <div class="flex gap-1.5 items-center">
          <Input bind:value={startYear} placeholder="Year" class="w-16" />
          <label class="flex items-center gap-1 text-xs text-text-secondary cursor-pointer whitespace-nowrap" class:text-accent-primary={startIsBc}>
            <Checkbox bind:checked={startIsBc} />
            BC
          </label>
          <Input bind:value={startMonth} placeholder="Mo" class="w-11" disabled={!startDateEnabled} />
          <Input bind:value={startDay} placeholder="Day" class="w-11" disabled={!startDateEnabled} />
        </div>
      </div>
      <div>
        <Label>End date (optional)</Label>
        <div class="flex gap-1.5 items-center">
          <Input bind:value={endYear} placeholder="Year" class="w-16" />
          <label class="flex items-center gap-1 text-xs text-text-secondary cursor-pointer whitespace-nowrap" class:text-accent-primary={endIsBc}>
            <Checkbox bind:checked={endIsBc} />
            BC
          </label>
          <Input bind:value={endMonth} placeholder="Mo" class="w-11" disabled={!endDateEnabled} />
          <Input bind:value={endDay} placeholder="Day" class="w-11" disabled={!endDateEnabled} />
        </div>
      </div>
    </div>

    <div>
      <Label for="participants">Participants (comma-separated)</Label>
      <Input id="participants" bind:value={participants} placeholder="Richard Nixon, John F. Kennedy" />
    </div>

    <div class="flex items-center gap-2">
      <Checkbox bind:checked={isPersonalEvent} />
      <span class="text-sm text-text-secondary">Personal event (birth, death, etc.)</span>
    </div>

    <div class="flex items-center gap-2">
      <Checkbox bind:checked={internalOnly} />
      <span class="text-sm text-text-secondary">Internal only (don't match cross-video)</span>
    </div>

    <div class="flex gap-2 pt-4 border-t border-border-subtle">
      {#if event?.id && onDelete}
        <Button variant="danger" onclick={handleDelete} disabled={isDeleting}>
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      {/if}
      <div class="flex-1"></div>
      <Button variant="ghost" onclick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onclick={handleSave} disabled={isSaving || !name.trim() || !startYear.trim()}>
        {isSaving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  </div>
</Dialog>
